/*
 * Cart drawer tool panels: Note / Gift / Shipping calculator / Discount code,
 * plus the drawer's Checkout button.
 *
 * These custom elements had been commented out and the file overwritten with a
 * duplicate of the free-gift sync script, which left every tool button in the
 * cart drawer inert. The free-gift script lives in layout/theme.liquid only.
 *
 * Discount codes are attached to the cart itself through the Cart API
 * (POST /cart/update.js { discount }) and read back from cart.discount_codes.
 * The previous implementation hit /discount/CODE, which stores the code in a
 * session cookie that Shopify then stamps onto every later cart - so a code a
 * shopper (or a tester) applied once kept reappearing on fresh carts with no
 * way to see or remove it. It also echoed the code through localStorage on
 * every visit. Both are gone: a code is on the cart only because the shopper
 * applied it, it is shown in the drawer with a Remove link, and checkout reads
 * the same cart, so the two can never disagree.
 */

(function () {
  var JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

  function closeOpenPanel() {
    var open = document.querySelector('.popup-toolDown.show');
    if (open) open.classList.remove('show');

    var preview = document.querySelector('.previewCart');
    if (preview) preview.classList.remove('active-tool');
  }

  function refreshCartDrawer() {
    try {
      if (
        typeof Shopify !== 'undefined' &&
        Shopify.getCart &&
        window.sharedFunctions &&
        window.sharedFunctions.updateSidebarCart
      ) {
        Shopify.getCart(function (cart) {
          window.sharedFunctions.updateSidebarCart(cart);
        });
      }
    } catch (error) {
      console.error('Cart drawer refresh failed:', error);
    }
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (res) {
      return res.json();
    });
  }

  /* Writes the cart's discount codes. An empty string removes them all. */
  function setCartDiscount(codes) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ discount: codes })
    }).then(function (res) {
      if (!res.ok) throw new Error('cart/update.js responded ' + res.status);
      return res.json();
    });
  }

  function applicableCodes(cart) {
    return (cart.discount_codes || [])
      .filter(function (d) { return d.applicable; })
      .map(function (d) { return d.code; });
  }

  /*
   * How long the Checkout button may wait on the cart lookup below, and how
   * long it may show a spinner at all.
   *
   * Round 77: getCart() had no timeout. A /cart.js that never settles left the
   * promise chain in checkoutUrl() unresolved, so no navigation was attempted
   * and `is-loading` was never removed - a Checkout button that spins forever
   * and does nothing, which is what the client reported. Both budgets below
   * are fail-safe: they can only ever turn a hang into the ordinary /checkout
   * navigation, never the other way round.
   */
  var CHECKOUT_LOOKUP_MS = 2500;
  var CHECKOUT_SPINNER_MS = 12000;

  /* Resolves with `fallback` if `promise` has not settled within `ms`. Never
   * rejects - the caller navigates with whatever comes back. */
  function withTimeout(promise, ms, fallback) {
    return new Promise(function (resolve) {
      var settled = false;
      var finish = function (value) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value);
      };
      var timer = setTimeout(function () { finish(fallback); }, ms);

      promise.then(finish, function () { finish(fallback); });
    });
  }

  /*
   * Keep checkout on this store's own domain.
   *
   * Round 79. Shopify's Shop Pay Universal Redirect hands a recognised Shop Pay
   * shopper from /checkout to shop.app to pay. Reproduced in the client's
   * browser: shop.app answers ERR_CONNECTION_RESET on that network, so checkout
   * ended on a browser error page - the storefront and cart were fine, only
   * checkout was unreachable, on live and preview alike.
   *
   * `skip_shop_pay=true` is Shopify's own opt-out; it is the parameter Shopify
   * puts in the `ur_back_url` of that very redirect. With it, /checkout resolves
   * to civiajewels.com/checkouts/cn/<token> and renders normally - measured.
   *
   * Shop Pay is NOT disabled: it stays available as a payment method on the
   * checkout page. Only the automatic hand-off to shop.app is skipped, which is
   * the hop that cannot be relied on.
   */
  function withSkipShopPay(url) {
    if (!url || /[?&]skip_shop_pay=/.test(url)) return url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'skip_shop_pay=true';
  }

  /*
   * Is this page a theme PREVIEW rather than the published theme?
   *
   * Two shapes, and round 78 exists because only the first was handled:
   *   - the admin's own preview host, *.shopifypreview.com
   *   - the live domain carrying ?preview_theme_id=<id>, which is the link
   *     shape people actually share for review
   *
   * `Shopify.theme.role` distinguishes them without parsing the URL, and keeps
   * working if the shopper navigates on within the preview session (Shopify
   * carries the preview forward without keeping the query string on every
   * link). Measured: "main" on the published theme, "unpublished" on a preview.
   *
   * Falls back to the hostname test alone if Shopify.theme is unavailable, so a
   * missing global can only ever mean "not a preview" - i.e. today's behaviour.
   */
  function isThemePreview() {
    if (/\.shopifypreview\.com$/i.test(location.hostname)) return true;

    var theme = window.Shopify && window.Shopify.theme;
    var role = theme && theme.role;

    return !!role && String(role).toLowerCase() !== 'main';
  }

  /*
   * Where the Checkout button should go.
   *
   * On the store's own domain, /checkout reads the cart - codes included.
   * Theme previews - both *.shopifypreview.com and ?preview_theme_id= on the
   * live domain - cannot carry a checkout: it is handed across to the real
   * domain (and, for Shop Pay shoppers, out to shop.app and back) by a
   * redirect that carries the items but drops the discount codes, which made
   * the preview look broken. From a preview host we go through Shopify's
   * cart-by-token permalink on the store's own domain instead: it recreates
   * the same cart (quantities and line properties included) and applies the
   * codes from the URL, then lands on checkout.
   */
  function checkoutUrl(fallback) {
    if (!isThemePreview()) return Promise.resolve(fallback);

    return getCart().then(function (cart) {
      var shop = window.Shopify && Shopify.shop;
      if (!shop || !cart.token) return fallback;

      var url = 'https://' + shop + '/cart/c/' + cart.token;
      var codes = applicableCodes(cart).join(',');
      if (codes) url += (url.indexOf('?') > -1 ? '&' : '?') + 'discount=' + encodeURIComponent(codes);
      return url;
    });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('#cart-sidebar-checkout');
    if (!button || button.disabled) return;
    event.preventDefault();

    var fallback = button.dataset.checkoutHref || '/checkout';
    button.classList.add('is-loading');

    /* Always settles - see withTimeout above. The spinner is cleared on a
     * watchdog so a stalled navigation cannot leave a dead button behind;
     * when the navigation succeeds this page is gone before it fires. */
    withTimeout(checkoutUrl(fallback), CHECKOUT_LOOKUP_MS, fallback)
      .then(function (url) {
        window.setTimeout(function () { button.classList.remove('is-loading'); }, CHECKOUT_SPINNER_MS);
        window.location.href = withSkipShopPay(url || fallback);
      });
  });

  /*
   * Take the Checkout button out of its loading state when the page comes back.
   *
   * The click handler above adds `is-loading` and then navigates, and nothing
   * removes it - normally the page is gone a moment later so it never matters.
   * It matters when the shopper starts checkout and then comes back: the page
   * is restored from the browser's back/forward cache with the DOM exactly as
   * it was left, `is-loading` included, and no load or DOMContentLoaded event
   * fires on a bfcache restore. The spinner sat there turning forever and the
   * button read as dead - visible in the client's recording at 5s, 8s, 11s
   * after the first return, and again after the second.
   *
   * `pageshow` is the one event that fires on BOTH a fresh load and a restore.
   * Cleared unconditionally rather than gated on `event.persisted`, because
   * Safari has cases where the DOM is reused and `persisted` reads false; on a
   * genuinely fresh load the class is not there, so this is a no-op.
   *
   * The selector is deliberately a little wider than the click handler's, so a
   * drawer that has been re-rendered by a cart update is covered too. Clearing
   * a loading state on page restore is always correct: nothing can still be
   * in flight across a navigation.
   */
  window.addEventListener('pageshow', function () {
    var buttons = document.querySelectorAll('#cart-sidebar-checkout, .button-checkout');
    Array.prototype.forEach.call(buttons, function (button) {
      button.classList.remove('is-loading');
    });
  });

  /* Opens the panel named by the clicked tool button's data-popup. */
  class CartTool extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.addEventListener('click', (event) => {
        var button = event.target.closest('.cartTool-item');
        if (!button || !this.contains(button)) return;

        var panel = document.getElementById(button.dataset.popup);
        if (!panel) return;

        closeOpenPanel();
        panel.classList.add('show');

        var preview = document.querySelector('.previewCart');
        if (preview) preview.classList.add('active-tool');
      });
    }
  }

  /* The Cancel button inside every tool panel. */
  class CartCancel extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.addEventListener('click', (event) => {
        if (!event.target.closest('button')) return;
        closeOpenPanel();
      });
    }
  }

  /* Saves the cart note. */
  class CartNote extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.addEventListener('click', (event) => {
        if (!event.target.closest('[data-update-note]')) return;

        var field = this.querySelector('.text-area');
        if (!field) return;

        fetch('/cart/update.js', {
          method: 'POST',
          headers: JSON_HEADERS,
          body: JSON.stringify({ note: field.value })
        }).catch(function (error) {
          console.error('Saving the cart note failed:', error);
        });

        closeOpenPanel();
      });
    }
  }

  /* Applies a discount code to the cart and refreshes the drawer totals. */
  class CouponCode extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      var input = this.querySelector('input[name="discount"]');
      if (input) {
        input.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this.apply();
          }
        });
      }

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-update-coupon]')) {
          event.preventDefault();
          this.apply();
        }
      });
    }

    showError(message) {
      var box = this.querySelector('[data-discount-error]');
      if (!box) return;
      box.textContent = message;
      box.hidden = !message;
    }

    apply() {
      var input = this.querySelector('input[name="discount"]');
      var button = this.querySelector('[data-update-coupon]');
      var code = input ? input.value.trim() : '';
      if (!code || this._busy) return;

      this._busy = true;
      if (button) button.disabled = true;
      this.showError('');

      var previous = [];

      getCart()
        .then(function (cart) {
          previous = applicableCodes(cart);
          return setCartDiscount(code);
        })
        .then((cart) => {
          var match = (cart.discount_codes || []).filter(function (d) {
            return d.code.toLowerCase() === code.toLowerCase();
          })[0];

          if (match && match.applicable) {
            closeOpenPanel();
            refreshCartDrawer();
            return;
          }

          // Shopify keeps an unusable code attached (applicable: false), which
          // would still travel to checkout. Put the cart back how it was.
          return setCartDiscount(previous.join(',')).then(() => {
            this.showError("This code can't be applied to your cart.");
          });
        })
        .catch((error) => {
          console.error('Applying the discount code failed:', error);
          this.showError('Something went wrong. Please try again.');
        })
        .then(() => {
          this._busy = false;
          if (button) button.disabled = false;
        });
    }
  }

  /* Remove links next to an applied code, rendered by snippets/halo-side-cart.liquid. */
  document.addEventListener('click', function (event) {
    var link = event.target.closest('[data-remove-discount]');
    if (!link) return;
    event.preventDefault();

    var removing = link.dataset.removeDiscount;
    link.disabled = true;

    getCart()
      .then(function (cart) {
        var keep = applicableCodes(cart).filter(function (c) {
          return c.toLowerCase() !== removing.toLowerCase();
        });
        return setCartDiscount(keep.join(','));
      })
      .then(function () {
        refreshCartDrawer();
      })
      .catch(function (error) {
        console.error('Removing the discount code failed:', error);
        link.disabled = false;
      });
  });

  if (!customElements.get('cart-item-tool')) customElements.define('cart-item-tool', CartTool);
  if (!customElements.get('cart-cancel-popup')) customElements.define('cart-cancel-popup', CartCancel);
  if (!customElements.get('cart-note')) customElements.define('cart-note', CartNote);
  if (!customElements.get('coupon-code')) customElements.define('coupon-code', CouponCode);
})();
